import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/components';

import {
  DETAILS_SETTINGS_MODAL_TYPES,
  VECTOR_FILE_EDITOR_MODES,
} from 'constants/all';

import { detailsSettingsModalFlow } from 'modules/common/modals/detailsSettingsModal/data/sagas';
import {
  fileUploadSaga,
  fileUploadDetailsModal,
} from 'modules/common/modals/uploadDetailsModal/data/sagas';
import * as collaboratorsSagas from 'modules/quotes/collaborators/data/sagas';
import { invalidateAllAssemblyTreeQueries } from 'modules/quotes/quoteItemComponents/assemblyTree/data/utils';
import {
  briefComponent,
  componentStatuses,
  componentUpdatePending,
  refreshBomRollup,
} from 'modules/quotes/quoteItems/data/actions';
import { getRootComponentIdByQuoteItemId } from 'modules/quotes/quoteItems/data/attributeSelectors/quoteItems';
import { getSimilarPartsKey } from 'modules/quotes/quoteItems/data/hooks';
import { refreshComponent } from 'modules/quotes/quoteItems/data/sagas';
import {
  getExpandedQuoteItem,
  getQuoteItemFromComponentId,
  getQuoteItemIdFromComponentId,
} from 'modules/quotes/quoteItems/data/selectors';
import quoteItemService from 'modules/quotes/quoteItems/data/service';
import { invalidateAllBomTableQueries } from 'modules/quotes/quoteItems/data/utils/bomTable';
import { DIRECTIONS as BOM_RESTRUCTURING_DIRECTIONS } from 'modules/quotes/quoteItems/modals/BOMRestructuring/data/constants';
import { openPopOutViewerFunc } from 'modules/quotes/utils/popOutViewer';

import partService from 'services/partService';
import quoteService from 'services/quoteService';

import { receiveSupplierMaterialFromGetOrCreate } from 'store/materials/actions';
import { getPart } from 'store/part/selectors';
import { getFormData } from 'store/part/utils';
import { refreshQuote } from 'store/quotes/actions';
import {
  getQuoteNumberWithRevision,
  getQuoteNumberRevisionNumberOrderPageCompatible,
} from 'store/quotes/selectors';

import { queryClient } from 'utils/QueryClientProvider';
import history from 'utils/browserHistory';
import { getErrorMessage, pluralize } from 'utils/stringUtils';

import { assemblyTree } from '../assemblyTree/data/actions';
import {
  attachFiles,
  bulkComponentUpdate,
  componentInterrogation,
  convertToManufacturedComponent,
  createManufacturedComponent,
  deleteComponents,
  editContours,
  editUnits,
  expandComponent,
  openVectorFileEditor,
  pageVectorization,
  removeAttachedFile,
  selectComponent,
  setComponentNodeQuantity,
  switchPrimaryFile,
  types,
  uploadComponent,
} from './actions';
import { getExpandedComponentId } from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.ON_BOM_RESTRUCTURE, watchOnBomRestructure);
  yield takeLatest(types.EXPAND_COMPONENT_REQUEST, watchExpandComponent);
  yield takeLatest(types.COLLAPSE_COMPONENT, watchCollapseComponent);
  yield takeLatest(types.EXPAND_COMPONENT_CLEAR, watchCollapseComponent);
  yield takeEvery(types.UPLOAD_COMPONENT_REQUEST, watchComponentUpload);
  yield takeEvery(
    types.COMPONENT_INTERROGATION_REQUEST,
    watchComponentInterrogations,
  );
  yield takeLatest(types.EDIT_UNITS_REQUEST, watchEditUnits);
  yield takeEvery(types.ATTACH_FILES_REQUEST, watchAttachFiles);
  yield takeEvery(types.REMOVE_ATTACHED_FILE_REQUEST, watchRemoveAttachedFile);
  yield takeLatest(types.DELETE_COMPONENTS_REQUEST, watchDeleteComponents);
  yield takeLatest(
    types.SET_COMPONENT_NODE_QUANTITY_REQUEST,
    watchSetComponentNodeQuantity,
  );
  yield takeLatest(
    types.CONVERT_TO_MANUFACTURED_COMPONENT_REQUEST,
    watchConvertToManufacturedComponent,
  );
  yield takeLatest(
    types.BULK_COMPONENT_UPDATE_REQUEST,
    watchBulkUpdateComponent,
  );
  yield takeLatest(
    types.CREATE_MANUFACTURED_COMPONENT_REQUEST,
    watchCreateManualComponent,
  );
  yield takeLatest(types.EDIT_CONTOUR_REQUEST, watchEditContourRequest);
  yield takeLatest(
    types.PAGE_VECTORIZATION_REQUEST,
    watchPageVectorizationRequest,
  );
  yield takeEvery(types.OPEN_VECTOR_FILE_EDITOR, watchOpenVectorFileEditor);
  yield takeEvery(types.OPEN_POP_OUT_VIEWER, watchOpenPopOutViewer);
  yield takeLatest(types.SWITCH_PRIMARY_FILE_REQUEST, watchSwitchPrimaryFile);
}

function* watchSetComponentNodeQuantity(action) {
  const { childComponentId, parentComponentId, quantity } = action.payload;
  if (
    yield call(collaboratorsSagas.areQuoteItemsLocked, {
      componentIds: [childComponentId],
    })
  )
    return;
  try {
    const quoteItem = yield select(
      getQuoteItemFromComponentId,
      childComponentId,
    );
    const response = yield call(
      quoteService.components.setComponentNodeQuantity,
      quoteItem?.id,
      {
        childId: childComponentId,
        parentId: parentComponentId,
        count: quantity,
      },
    );
    yield put(
      briefComponent.receive({
        briefComponent: response,
        quoteItemId: quoteItem?.id,
      }),
    );
    yield put(
      refreshBomRollup.request({
        parentComponentIds: response.parentComponentIds,
        delayTime: 0,
        shouldFullyRefreshRootOrExpandedComponents: true,
      }),
    );
  } catch (e) {
    yield put(setComponentNodeQuantity.error(e));
  }
  yield put(assemblyTree.markNeedsUpdate({}));
}

function* watchCollapseComponent() {
  const quoteNumber = yield select(getQuoteNumberWithRevision);
  const expandedQuoteItem = yield select(getExpandedQuoteItem);
  if (!expandedQuoteItem) return;

  const { id: quoteItemId } = expandedQuoteItem;
  const search = new URLSearchParams(location.search);
  search.delete('nodeId');

  history.push({
    pathname: `/quotes/edit/${quoteNumber}/${quoteItemId}`,
    search: `?${search}`,
  });
}

function* watchExpandComponent(action) {
  const { componentId, nodeId } = action.payload;
  try {
    if (!location.pathname.includes(componentId.toString())) {
      const quoteNumber = yield select(getQuoteNumberWithRevision);
      const { id: quoteItemId } = yield select(getExpandedQuoteItem);
      const search = new URLSearchParams(location.search);
      if (nodeId) {
        search.set('nodeId', nodeId);
      } else {
        search.delete('nodeId');
      }

      history.push({
        pathname: `/quotes/edit/${quoteNumber}/${quoteItemId}/${componentId}`,
        search: `?${search}`,
      });
    }

    const component = yield call(
      quoteService.components.get,
      componentId,
      nodeId,
    );
    yield put(expandComponent.success({ component }));
    queryClient.invalidateQueries(getSimilarPartsKey(componentId));
  } catch (error) {
    yield put(expandComponent.error({ componentId, error }));
    yield call(toast, {
      status: 'error',
      title: "Can't open component details",
    });
  }
}

function* watchComponentInterrogations(action) {
  const { componentId } = action.payload;
  try {
    const response = yield call(
      quoteItemService.components.interrogationRequest,
      componentId,
    );
    const nextInterrogationStatuses = {
      [componentId.toString()]: response,
    };
    yield put(
      componentStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
    yield put(componentInterrogation.success());
  } catch (error) {
    yield put(componentInterrogation.error(error));
  }
}

function* watchEditUnits(action) {
  const { filename, componentId, fileUuid, units } = action.payload;

  const details = yield call(fileUploadDetailsModal, {
    filename,
    shouldEnterThickness: false,
    shouldEnterUnits: true,
    similarFiles: [],
    currentUnits: units,
  });

  if (details && details.units !== units) {
    try {
      yield call(partService.partFiles.update, fileUuid, {
        units: details.units,
      });

      // refresh component to get new data
      const newComponent = yield call(refreshComponent, componentId);
      queryClient.invalidateQueries(getSimilarPartsKey(componentId));

      // refresh parent components in case its a manual child component
      yield put(
        refreshBomRollup.request({
          parentComponentIds: newComponent.parentComponentIds,
        }),
      );

      yield put(editUnits.success());
    } catch (error) {
      yield put(editUnits.error(error));
    }
  } else {
    yield put(editUnits.success());
  }
}

function* watchAttachFiles(action) {
  const {
    partId,
    quoteItemId,
    orderItemId,
    partMasterVersion,
    files,
    shouldSetPrimaryFile,
  } = action.payload;
  try {
    if (quoteItemId) {
      if (
        yield call(collaboratorsSagas.areQuoteItemsLocked, {
          quoteItemIds: [quoteItemId],
        })
      ) {
        return yield put(attachFiles.error(partId));
      }
    }

    const uploadSagaInput = [];
    for (const possibleFile of files) {
      uploadSagaInput.push(possibleFile);
    }

    const fileInfoList = yield call(fileUploadSaga, uploadSagaInput);
    if (fileInfoList.length === 0) {
      return yield put(attachFiles.cancel(partId));
    }

    const formData = new FormData();
    const uploadData = {};
    for (const possibleFile of fileInfoList) {
      formData.append(possibleFile.file.name, possibleFile.file);
      uploadData[possibleFile.file.name] = {
        units: possibleFile.units,
        thickness: possibleFile.thickness,
      };
    }
    formData.append('upload_data', JSON.stringify(uploadData));

    if (orderItemId) {
      formData.append('order_item_id', orderItemId);
    }
    if (quoteItemId) {
      formData.append('quote_item_id', quoteItemId);
    }
    formData.append('part_master_version', partMasterVersion);
    if (shouldSetPrimaryFile !== undefined) {
      formData.append('should_set_primary_file', shouldSetPrimaryFile);
    }

    const nextPart = yield call(partService.attachFile, partId, formData);

    if (shouldSetPrimaryFile) {
      const rootComponentId = yield select(
        getRootComponentIdByQuoteItemId,
        quoteItemId,
      );
      const response = yield call(partService.checkStatuses, [
        nextPart.primaryFileUuid,
      ]);
      const primaryFileProcessingStatus = response.statuses.processing[0];
      yield put(
        componentStatuses.receiveProcessingStatuses({
          processingStatuses: {
            [rootComponentId.toString()]: primaryFileProcessingStatus,
          },
        }),
      );
    }

    yield put(attachFiles.success(nextPart));
    yield call(toast, {
      title: `Attached ${pluralize('file', fileInfoList.length)}`,
      status: 'success',
    });
  } catch (error) {
    yield put(attachFiles.error(partId));
    yield call(toast, {
      title: 'Failed to attach file',
      description: getErrorMessage(error),
      status: 'error',
    });
  }
}

function* watchRemoveAttachedFile(action) {
  const { partId, quoteItemId, fileId } = action.payload;
  const part = yield select(getPart, partId);
  const { partFiles } = part;
  try {
    if (quoteItemId) {
      if (
        yield call(collaboratorsSagas.areQuoteItemsLocked, {
          quoteItemIds: [quoteItemId],
        })
      ) {
        return yield put(removeAttachedFile.error(partId));
      }
    }
    const nextFiles = partFiles.filter((file) => file.uuid !== fileId);
    const nextPart = { ...part, partFiles: nextFiles };
    // perform optimistic update
    yield put(removeAttachedFile.success(nextPart));
    yield call(partService.removeAttachedFile, fileId, { partId: partId });
  } catch (error) {
    // rollback update if error occured
    const part = yield select(getPart, partId);
    const nextPart = { ...part, partFiles };
    yield put(removeAttachedFile.error(nextPart));
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  }
}

function* watchDeleteComponents(action) {
  const { quoteItemId, nodeIds, componentIds } = action.payload;
  try {
    const response = yield call(
      quoteItemService.deleteComponents,
      quoteItemId,
      // if no nodeIds are present, fallback to componentIds
      nodeIds ? { nodeIds } : { componentIds },
    );
    if (nodeIds) {
      // child nodes were deleted, refresh the parent
      const expandedComponentId = yield select(getExpandedComponentId);
      if (expandedComponentId) {
        yield call(refreshComponent, expandedComponentId);
      }
    }
    yield put(deleteComponents.success(response));
  } catch (error) {
    yield put(deleteComponents.error(error));
  }
  invalidateAllBomTableQueries();
  yield put(assemblyTree.markNeedsUpdate({}));
}

function* watchOpenPopOutViewer({ payload }) {
  const {
    activePartUuid,
    isMaterialWidgetSelected,
    partMasterUuid,
    partMasterVersion,
    quoteItemId,
    selectedFileUuid,
  } = payload;

  const quoteNumberRevisionNumber = yield select(
    getQuoteNumberRevisionNumberOrderPageCompatible,
  );

  openPopOutViewerFunc({
    activePartUuid,
    isMaterialWidgetSelected,
    partMasterUuid,
    partMasterVersion,
    quoteItemId,
    selectedFileUuid,
    quoteNumberRevisionNumber,
  });
}

function watchOpenVectorFileEditor({ payload }) {
  const { activeId, mode, partMasterVersion, partUuid } = payload;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const width = parseInt(screenWidth * 0.9);
  const height = parseInt(screenHeight * 0.9);
  const features = `width=${width},height=${height},menubar=yes,toolbar=yes,location=yes`;
  const url = `/vectorFileEditor/${mode}/${activeId}?part=${partUuid}&version=${partMasterVersion}`;
  window.open(url, 'vectorFileEditor', features);
}

function* watchComponentUpload(action) {
  const { files, quoteItemId, parentComponentId } = action.payload;

  try {
    const fileInfoList = yield call(fileUploadSaga, [].concat(files));

    if (fileInfoList.length === 0) {
      return;
    }

    const quoteSettings = yield call(detailsSettingsModalFlow, {
      isMultipleItems: fileInfoList.length > 1,
      mode: DETAILS_SETTINGS_MODAL_TYPES.COMPONENT_UPLOAD,
      filenames: Object.values(fileInfoList).flatMap(({ file }) => file.name),
    });

    if (!quoteSettings) {
      return;
    }

    yield put(componentUpdatePending.start());

    // upload all files and set real part id
    let parentComponentIds;
    for (const fileInfo of fileInfoList) {
      const formData = yield call(uploadComponentSaga, {
        file: fileInfo.file,
        thickness: fileInfo.thickness,
        units: fileInfo.units,
        quoteSettings,
        parentComponentId,
      });
      const response = yield call(
        quoteItemService.uploadComponent,
        quoteItemId,
        formData,
      );
      yield put(briefComponent.add({ component: response, quoteItemId }));
      if (!parentComponentIds) {
        parentComponentIds = response.parentComponentIds;
      }
    }

    yield put(uploadComponent.success());
    yield call(toast, {
      title: `Uploaded ${pluralize('component', fileInfoList.length)}`,
      status: 'success',
    });

    yield put(
      refreshBomRollup.request({
        parentComponentIds: parentComponentIds ?? [],
        shouldFullyRefreshRootOrExpandedComponents: true,
      }),
    );
  } catch (error) {
    yield put(uploadComponent.error(error));
  }
  yield put(componentUpdatePending.finish());
}

function* uploadComponentSaga({
  file,
  thickness,
  units,
  quoteSettings,
  parentComponentId,
}) {
  try {
    return yield call(uploadComponentFlow, {
      file,
      thickness,
      units,
      quoteSettings,
      parentComponentId,
    }) || {};
  } catch (error) {
    toast({
      status: 'error',
      title: `Can't upload ${file.name}.`,
      description: error.message,
    });
  }
}

function uploadComponentFlow({
  file,
  thickness,
  units,
  quoteSettings,
  parentComponentId,
}) {
  const data = {
    units,
    thickness,
    file,
    partNumber: quoteSettings.partNumber,
    revision: quoteSettings.revision,
    processId: quoteSettings.process,
    materialId: quoteSettings.material,
    supplierMaterialId: quoteSettings.supplierMaterial,
    finishOpDefIds: quoteSettings.finishOpDefIds,
    bomQuantity: quoteSettings.bomQuantity,
    parentComponentId,
  };
  return getFormData(data);
}
function* watchConvertToManufacturedComponent(action) {
  const { payload } = action;
  const { componentId } = payload;

  yield put(componentUpdatePending.start());

  try {
    const response = yield call(
      quoteItemService.components.convertToManufacturedComponent,
      componentId,
    );

    yield put(briefComponent.receive({ briefComponent: response }));
    yield put(
      refreshBomRollup.request({
        parentComponentIds: response.parentComponentIds,
        delayTime: 0,
        shouldFullyRefreshRootOrExpandedComponents: true,
      }),
    );

    yield put(convertToManufacturedComponent.success());
    yield put(selectComponent.deselectAll());
    yield call(toast, {
      status: 'success',
      title: 'Converted to Manufactured Component',
      description:
        'The purchased component was successfully converted to a manufactured component',
    });
  } catch (error) {
    yield put(convertToManufacturedComponent.error(error));
  }
  yield put(componentUpdatePending.finish());
  yield put(assemblyTree.markNeedsUpdate({}));
}

function* watchBulkUpdateComponent(action) {
  const { payload } = action;
  const { componentIds, quoteId } = payload;

  const firstQuoteItemId = yield select(
    getQuoteItemIdFromComponentId,
    componentIds[0],
  );

  const updateSettings = yield call(detailsSettingsModalFlow, {
    isMultipleItems: componentIds.length > 1,
    mode: DETAILS_SETTINGS_MODAL_TYPES.BULK_COMPONENT_UPDATE,
    filenames: [],
    bulkUpdateQuoteItemIds: firstQuoteItemId != null ? [firstQuoteItemId] : [],
  });

  if (!updateSettings) return;

  const { process, material, supplierMaterial, finishOpDefIds } =
    updateSettings;

  yield put(componentUpdatePending.start());

  try {
    const response = yield call(
      quoteItemService.bulkUpdateComponents,
      quoteId,
      {
        componentIds,
        processId: process,
        materialId: material,
        supplierMaterialId: supplierMaterial,
        finishOpDefIds,
      },
    );

    // need to update material store, this view assumes all materials set are
    // the same across all components
    const comp = response[0];
    if (comp.material) {
      const newSupplierMaterial = {
        ...comp.material,
        materialId: comp.material.material.id,
      };
      yield put(
        receiveSupplierMaterialFromGetOrCreate({
          supplierMaterial: newSupplierMaterial,
        }),
      );
    }

    yield put(briefComponent.receiveMultiple({ briefComponents: response }));

    // get unique parent component ids to refresh quantities for
    const parentCompIds = new Set();
    for (const briefComp of response) {
      for (const parentId of briefComp.parentComponentIds) {
        parentCompIds.add(parentId);
      }
    }

    if (process) {
      for (const componentId of componentIds) {
        yield put(componentInterrogation.request({ componentId }));
      }
    }

    yield all([
      put(
        refreshBomRollup.request({
          parentComponentIds: [...parentCompIds],
          delayTime: 0,
        }),
      ),
      put(selectComponent.deselectAll()),
      put(bulkComponentUpdate.success()),
    ]);
  } catch (error) {
    yield put(bulkComponentUpdate.error(error));
  }
  yield put(componentUpdatePending.finish());
}

function* watchCreateManualComponent(action) {
  const { payload } = action;
  const { quoteItemId, mode, parentComponentId, isAssembly } = payload;

  const updateSettings = yield call(detailsSettingsModalFlow, {
    isMultipleItems: false,
    mode: mode || DETAILS_SETTINGS_MODAL_TYPES.COMPONENT_CREATE,
  });

  if (!updateSettings) return;

  const {
    partNumber,
    revision,
    process,
    material,
    supplierMaterial,
    finishOpDefIds,
    bomQuantity,
  } = updateSettings;

  yield put(componentUpdatePending.start());

  try {
    const response = yield call(
      quoteItemService.createManualComponent,
      quoteItemId,
      {
        partNumber,
        revision,
        processId: process,
        materialId: material,
        supplierMaterialId: supplierMaterial,
        finishOpDefIds,
        bomQuantity,
        parentComponentId,
        isAssembly,
      },
    );

    yield put(briefComponent.add({ component: response, quoteItemId }));

    yield put(createManufacturedComponent.success());

    yield put(
      refreshBomRollup.request({
        parentComponentIds: response.parentComponentIds ?? [],
        shouldFullyRefreshRootOrExpandedComponents: true,
      }),
    );
  } catch (error) {
    yield put(createManufacturedComponent.error(error));
  }

  yield put(componentUpdatePending.finish());
}

function* watchEditContourRequest({ payload }) {
  const { partFileUuid, partMasterVersion, partUuid } = payload;

  try {
    const response = yield call(partService.editContourRequest, partFileUuid);
    yield put(
      openVectorFileEditor({
        activeId: response.id,
        mode: VECTOR_FILE_EDITOR_MODES.CONTOUR,
        partMasterVersion,
        partUuid,
      }),
    );
    yield put(editContours.success());
  } catch (error) {
    yield put(editContours.error(error));
  }
}

function* watchPageVectorizationRequest({ payload }) {
  const { partMasterVersion, partUuid, printPageId } = payload;

  try {
    const response = yield call(
      partService.pageVectorizationRequest,
      printPageId,
    );
    yield put(
      openVectorFileEditor({
        activeId: response.id,
        mode: VECTOR_FILE_EDITOR_MODES.VECTORIZE,
        partMasterVersion,
        partUuid,
      }),
    );
    yield put(pageVectorization.success());
  } catch (error) {
    yield put(pageVectorization.error(error));
  }
}

function* watchSwitchPrimaryFile({ payload }) {
  const {
    componentId,
    partFileUuid,
    partUuid,
    shouldRegenerateOperations,
    oldName,
    newFilename,
  } = payload;
  const data = {
    partFileUuid,
    shouldRegenerateOperations,
  };
  try {
    const response = yield call(partService.switchPrimaryFile, partUuid, data);
    const nextInterrogationStatuses = {
      [componentId.toString()]: response,
    };
    yield put(
      componentStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
    const component = yield call(refreshComponent, componentId);
    yield put(assemblyTree.markNeedsUpdate({}));
    yield put(
      refreshBomRollup.request({
        parentComponentIds: component.parentComponentIds,
      }),
    );

    yield put(switchPrimaryFile.success({ componentId, oldName, newFilename }));
  } catch (error) {
    yield put(switchPrimaryFile.error({ componentId, error }));
  }
}

function* watchOnBomRestructure({ payload }) {
  try {
    const { direction, shouldRefreshQuote } = payload;

    if (direction === BOM_RESTRUCTURING_DIRECTIONS.UP && shouldRefreshQuote) {
      // need to refresh the quote in order to pull in the new quote items
      yield put(refreshQuote.request());
    }
    yield put(selectComponent.deselectAll());
    invalidateAllAssemblyTreeQueries();
    invalidateAllBomTableQueries();
  } catch (error) {
    console.error('There was an error in watchOnBomRestructure', error);
  }
}
